/* eslint-disable no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'
import {
  Application,
  CPIManifest,
  ExclusionField,
  ExclusionRule,
  ExclusionRuleConditionInput,
  ExclusionRuleMatchType,
  FormWeightI,
  IntegrationInstance,
  ManifestIntegrationSupportedCPI,
  RecordBatchInputSchemaItem,
  TableData,
  User,
  UserRole,
  Vulnerability
} from '../../models'
import { PerformanceScoreCpi } from '../../models/PerformanceScoreChart'
import { BenchmarkScoreCategoryData } from '../../models/Benchmark'

export enum ModalType {
  jira = 'jira',
  auth0 = 'auth0',
  jiraTicket = 'jiraTicket',
  crowdstrike = 'crowdstrike',
  mssentinel = 'mssentinel',
  onboardingForm = 'onboardingForm',
  startOnboarding = 'startOnboarding',
  inviteUser = 'inviteUser',
  deleteUsers = 'deleteUsers',
  userProfile = 'userProfile',
  changeName = 'changeName',
  changeEmail = 'changeEmail',
  changeRole = 'changeRole',
  deleteAccount = 'deleteAccount',
  deleteIntegrationInstance = 'deleteIntegrationInstance',
  deleteApplicationInstance = 'deleteApplicationInstance',
  deactivateCpi = 'deactivateCpi',
  transferOwnership = 'transferOwnership',
  deselectNotification = 'deselectNotification',
  zoomIn = 'zoomIn',
  deleteDeactivation = 'deleteDeactivation',
  ssoSupport = 'ssoSupport',
  ssoVerificationFailed = 'ssoVerificationFailed',
  ssoDelete = 'ssoDelete',
  cancelChangesWeight = 'cancelChangesWeight',
  revertDefaultValuesWeight = 'revertDefaultValuesWeight',
  updateWeights = 'updateWeights',
  cancelSetupIntegrationChanges = 'cancelSetupIntegrationChanges',
  confirmDeleteFramework = 'confirmDeleteFramework',
  confirmResetFramework = 'confirmResetFramework',
  disconnectJiraProject = 'disconnectJiraProject',
  addCpisToIntegration = 'addCpisToIntegration',
  activateCpi = 'activateCpi',
  nexaAIToggle = 'nexaAIToggle',
  calculationDetails = 'calculationDetails',
  benchmarkingDetails = 'benchmarkingDetails',
  createExclusionRule = 'createExclusionRule',
  excludedRecords = 'excludedRecords',
  deleteExclusionRule = 'deleteExclusionRule',
  createTicket = 'createTicket'
}

export enum ModalVariant {
  add = 'add',
  edit = 'edit',
  view = 'view',
  delete = 'delete'
}

interface IZoomIn {
  name?: string
  url?: string
}

export interface ModalProps {
  vulnerability?: Vulnerability
  showTransferInput?: boolean
  task?: TableData
  integrationInstance?: IntegrationInstance
  cpiSupportedAndUserConfiguredList?: IntegrationInstance[]
  manifestData?: CPIManifest | null
  cpiName?: string
  integrationName?: string
  users?: User[]
  roles?: UserRole[]
  user?: User
  zoomIn?: IZoomIn
  callback?: () => void
  addCpisCallback?: (cpis: string[]) => void
  connection?: any
  isVerified?: boolean
  weightsData?: FormWeightI
  closeback?: () => void
  confirmNavigation?: any
  cancelNavigation?: any
  isAnalyzer?: boolean
  closeCallback?: () => void
  okCallback?: () => void
  cancelCallback?: () => void
  cpisList?: ManifestIntegrationSupportedCPI[]
  newRole?: string
  isSnowflake?: boolean
  nexaAIEnabled?: boolean
  nexaAIAgentName?: string
  cpiDisplayName?: string
  activateCpiCallback?: () => Promise<void>
  calculationDetailsData?: {
    cpis: PerformanceScoreCpi[]
    score: number
    lastUpdate: string
  }
  benchmarkingDetailsData?: {
    cpis: BenchmarkScoreCategoryData[]
    lastUpdate: string
    summary?: { less: number; equal: number; more: number }
  }
  createExclusionRuleProps?: {
    cpiSlug: string
    cpiDisplayId: string
    exclusionFields: ExclusionField[]
    recordIdentifier: string
    unitsLabel: string
    mode: 'single' | 'bulk' | 'filter' | 'empty' | 'edit'
    rule?: ExclusionRule
    initialConditions: ExclusionRuleConditionInput[]
    initialMatchType?: ExclusionRuleMatchType
    /** Raw instance_name -> configured display name, for fields that hold integration instances. */
    instanceDisplayNames?: Record<string, string>
    onSuccess?: () => void
  }
  createTicketProps?: {
    cpiSlug: string
    cpiDisplayId: string
    applications: Application[]
    recordIds: string[]
    /* The selected rows and the columns to show them under in the Summary step. */
    records: Record<string, any>[]
    recordColumns: { field: string; headerName: string }[]
    /* The selected rows' earliest start and latest close (YYYY-MM-DD), for Jira's Target start/end. */
    targetDates?: { start: string | null; end: string | null }
    onSuccess?: () => void
  }
  excludedRecordsProps?: {
    ruleId: string
    cpiSlug: string
    ruleName: string
    reason: string | null
    createdAt: string
    createdBy: string
    lastEditedAt?: string | null
    updatedBy?: string | null
    inputSchema?: RecordBatchInputSchemaItem[]
    lastUpdated?: string | null
    dataSourceOptions?: IntegrationInstance[]
    /* Absent when the viewer may not edit, which hides the header's edit button. */
    onEdit?: () => void
  }
  deleteApplicationInstanceProps?: {
    displayName: string
    onConfirm: () => Promise<void>
  }
  deleteExclusionRuleProps?: {
    ruleIds: string[]
    ruleName?: string
    onSuccess?: () => void
  }
}

export interface ModalState {
  type?: ModalType
  open: boolean
  variant: ModalVariant
  props?: ModalProps
}

const initialState: ModalState = {
  type: undefined,
  open: false,
  variant: ModalVariant.add,
  props: undefined
}

export const modalSlice = createSlice({
  name: 'Modals',
  initialState,
  reducers: {
    openModal: (
      state: ModalState,
      action: PayloadAction<{
        type?: ModalType
        variant?: ModalVariant
        props?: ModalProps
      }>
    ) => {
      const { type, variant, props } = action.payload

      state.type = type
      state.open = true
      state.variant = variant ?? ModalVariant.add
      state.props = props
    },
    closeModal: (state: ModalState) => {
      state.type = undefined
      state.open = false
    },
    setModalProps: (
      state: ModalState,
      action: PayloadAction<{
        type?: ModalType
        variant?: ModalVariant
        props?: ModalProps
        open: boolean
      }>
    ) => {
      const { type, variant, props, open } = action.payload

      state.type = type
      state.open = open
      state.variant = variant ?? ModalVariant.add
      state.props = props
    }
  }
})

export const modalPropsSelector = (state: RootState): ModalState => state.modals

export const { openModal, closeModal, setModalProps } = modalSlice.actions

export default modalSlice.reducer
